import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { researchKeys } from '~/lib/query-keys';

export type ResearchStatus = 'running' | 'completed' | 'failed' | 'cancelled';

/**
 * FRONT-1578: the research engine's five real stages, in the order it moves
 * through them. `filling` (gap-filling) is CONDITIONAL — a run that found no
 * gaps to review skips it outright, so its absence from a run's stage
 * history is a legitimate "skipped," never a sign of data loss.
 */
export type ResearchStageKey =
  | 'planning'
  | 'researching'
  | 'reviewing'
  | 'filling'
  | 'writing';

export interface ResearchRun {
  id: number;
  zitadelOrgId: string;
  userId: string;
  sessionId: string;
  skillRunId: string;
  topic: string;
  status: ResearchStatus;
  startedAt: string;
  completedAt?: string;
  briefingDocFilename?: string;
  errorMessage?: string;
  /**
   * FRONT-1500: the research engine's current stage label ("Planning the
   * research…", "Researching…", "Reviewing gaps…", "Writing the
   * briefing…"). Absent before the engine's first stage callback fires and
   * on runs started before this field existed — callers must fall back to
   * static copy when it's missing.
   */
  phase?: string;
  /**
   * FRONT-1578: the engine's current stage, as a stable key rather than the
   * free-text `phase` label above — the chat's stage strip renders off THIS,
   * never off `phase` (which carries counts and phrasing that change per
   * run). Absent on a run started before this field existed, or before the
   * engine's first stage callback fires on a fresh run — never guessed from
   * `phase` text or from position, so callers must render nothing (no strip)
   * rather than a wrong one when it's missing.
   */
  stage?: ResearchStageKey;
  /**
   * FRONT-1578: whether this run is known to be on the DEEP research
   * pipeline rather than the standard one — a standard run can never reach
   * the `reviewing`/`filling` stages, so the chat's stage strip must only
   * show those two steps when this (or a stage event carrying it, or a
   * `gap-check` timeline event) says so. Absent — never `false` — when
   * there's no such evidence yet; callers must not treat absence as "this
   * is a standard run," only as "not yet known to be deep."
   */
  deep?: boolean;
  /**
   * FRONT-1601: set once a "Finish now" request has landed for this run
   * (`POST /api/research/:runId/finish`), null until then. Durable — rides
   * the run row rather than local mutation state — so a page reload still
   * shows the request as registered instead of offering the control again.
   */
  finishRequestedAt?: string | null;
}

export interface ResearchCitation {
  id: number;
  runId: number;
  url: string;
  title?: string;
  snippet?: string;
  fetchedAt: string;
  entityId?: string;
  attributeKey?: string;
  memoryDocFilename?: string;
}

interface ResearchResponse {
  run: ResearchRun | null;
}

/**
 * Poll cadence for a research run query. SSE `research-changed` keeps these
 * live in the happy path, but when a tab loses its SSE subscriber (dropped or
 * changed connection) no invalidation ever fires and the card freezes at its
 * last cached state — e.g. "Researching…" forever even though the run is long
 * done (FRONT-695). So we also poll the source-of-truth Postgres row while the
 * run is non-terminal, exactly like the background-chat status spine does
 * (`useBackgroundChatForSession`). Stops the moment the run settles.
 */
export function researchRefetchInterval(
  run: ResearchRun | null | undefined,
): number | false {
  return run?.status === 'running' ? 3_000 : false;
}

interface CitationsResponse {
  citations: ResearchCitation[];
}

/**
 * FRONT-1500: one row of a research run's durable, append-only timeline —
 * the work-log the chat's research banner renders. Mirrors
 * `src/research/events-store.ts`'s `ResearchEvent` (confirmed against the
 * live `GET /api/research/:runId/timeline` response, not just the backend
 * source — see the curl output in the PR/report).
 */
export interface ResearchTimelineEvent {
  id: number;
  runId: number;
  at: string;
  kind: 'plan' | 'search' | 'gap-check' | 'stage' | 'note';
  label: string;
  detail?: Record<string, unknown>;
  /**
   * FRONT-1578: present only on a `kind: 'stage'` row — the stage that event
   * announced. This is a run's durable, append-only STAGE HISTORY: the stage
   * strip reads it to tell "reached this stage" (a row exists for it) apart
   * from "never got here" (a purely positional guess would be wrong for the
   * conditional `filling` stage, which a run can legitimately skip).
   */
  stage?: ResearchStageKey;
  /**
   * FRONT-1578: present only on a `kind: 'stage'` row — whether THIS event's
   * own label is evidence the run is on the DEEP research pipeline (see
   * `ResearchRun.deep`). Absent — never `false` — for every non-stage row
   * and for a stage row whose label isn't itself deep-only evidence.
   */
  deep?: boolean;
}

interface TimelineResponse {
  events: ResearchTimelineEvent[];
}

/**
 * Current research run for a chat session — returns null when no run
 * has ever started. The SSE handler invalidates the `researchKeys.all`
 * tree on `research-changed` and `skill-progress` (filtered to
 * deep-research) so this stays live; we also poll while the run is `running`
 * as a fallback for when the tab has no live SSE subscriber (FRONT-695).
 */
export function useResearch(sessionId: string | undefined) {
  return useQuery({
    queryKey: sessionId ? researchKeys.bySession(sessionId) : researchKeys.all,
    queryFn: async () => {
      if (!sessionId) return null;
      const data = await api.get<ResearchResponse>(
        `/api/research/current?sessionId=${encodeURIComponent(sessionId)}`,
      );
      return data.run;
    },
    enabled: !!sessionId,
    refetchInterval: (q) =>
      researchRefetchInterval(q.state.data as ResearchRun | null | undefined),
  });
}

/**
 * A specific research run by its skillRunId (the stable per-run key each
 * inspector card carries). Unlike {@link useResearch}, which returns only the
 * session's LATEST run, this resolves THIS card's own run — so every stacked,
 * persistent green card can open its result, not just the most recent one.
 * Shares the `researchKeys.all` tree so SSE invalidation keeps it live, and
 * polls while `running` as the same no-subscriber fallback (FRONT-695).
 */
export function useResearchByRunId(skillRunId: string | undefined) {
  return useQuery({
    queryKey: skillRunId ? researchKeys.byRun(skillRunId) : researchKeys.all,
    queryFn: async () => {
      if (!skillRunId) return null;
      const data = await api.get<ResearchResponse>(
        `/api/research/by-run?runId=${encodeURIComponent(skillRunId)}`,
      );
      return data.run;
    },
    enabled: !!skillRunId,
    refetchInterval: (q) =>
      researchRefetchInterval(q.state.data as ResearchRun | null | undefined),
  });
}

/**
 * Citations for a given research run id. Used by the live card (count
 * badge) and the briefing message's SourcesBlock (when expanded). Pass
 * `enabled: false` to defer the fetch — SourcesBlock uses this on the
 * collapsed render so we don't load every briefing's sources at once.
 */
export function useResearchCitations(
  runId: number | undefined,
  options: { enabled?: boolean; live?: boolean } = {},
) {
  const enabled = options.enabled !== false && !!runId;
  return useQuery({
    queryKey: runId ? researchKeys.citations(runId) : researchKeys.all,
    queryFn: async () => {
      if (!runId) return [];
      const data = await api.get<CitationsResponse>(
        `/api/research/${runId}/citations`,
      );
      return data.citations;
    },
    enabled,
    // While a run is still working, the research ENGINE files sources as it
    // goes but emits no per-source event — so the count would otherwise sit at
    // 0 until the run finishes. Poll while `live` so the source counter on the
    // running card climbs in real time. Stops as soon as `live` goes false.
    refetchInterval: options.live ? 3_000 : false,
  });
}

/**
 * How many already-known ids to re-request on every incremental poll. Pure
 * insurance against the out-of-order-commit race documented on
 * {@link fetchResearchTimelinePage} — a handful of already-rendered rows
 * coming back again and being discarded by the id-keyed merge, not a
 * meaningful cost next to the request itself.
 */
const TIMELINE_POLL_OVERLAP = 3;

/**
 * FRONT-1500: fetch one incremental page of a run's timeline and merge it
 * onto what's already known. Exported (rather than inlined in the queryFn
 * below) so it's directly unit-testable without a real QueryClient — same
 * pattern as `fetchChatModelMode` in `use-chat-model-mode.ts`.
 *
 * `previous` must be exactly what's already been rendered/cached, in id
 * order: when it's non-empty we ask the API for rows starting a small
 * `TIMELINE_POLL_OVERLAP` below the highest id we already have, so a
 * long-running poll still costs one small delta request rather than
 * re-fetching the whole log every 3s.
 *
 * The overlap exists because `research_events.id` is a Postgres `serial`:
 * two concurrent inserts on separate connections can take ids 10 and 11 but
 * COMMIT in the opposite order, so a poll landing between those two commits
 * sees only id 11 and would, with a bare `afterId = 11`, never ask for id 10
 * again — it's permanently skipped even though it exists. Re-requesting the
 * last few known ids on every poll means a late-committing row still shows
 * up on the very next poll instead of being lost forever.
 *
 * The response is merged by id into a de-duplicated, id-ordered list (never
 * assumed to already be ordered or new) — this also makes an out-of-order
 * response (a lower id arriving after a higher one) render correctly rather
 * than out of chronological order. An empty response, or a response that
 * only repeats already-known ids (the overlap doing its job with nothing new
 * to report), returns the SAME `previous` array reference, not a new copy —
 * keeps consumers (`useQuery`'s `data`) referentially stable when nothing
 * changed.
 */
export async function fetchResearchTimelinePage(
  runId: number,
  previous: ResearchTimelineEvent[],
): Promise<ResearchTimelineEvent[]> {
  const maxKnownId = previous.length
    ? previous[previous.length - 1]!.id
    : undefined;
  const afterId =
    maxKnownId != null
      ? Math.max(0, maxKnownId - TIMELINE_POLL_OVERLAP)
      : undefined;
  const data = await api.get<TimelineResponse>(
    `/api/research/${runId}/timeline${afterId != null ? `?afterId=${afterId}` : ''}`,
  );
  if (data.events.length === 0) return previous;
  const merged = new Map(previous.map((event) => [event.id, event]));
  let grew = false;
  for (const event of data.events) {
    if (!merged.has(event.id)) grew = true;
    merged.set(event.id, event);
  }
  if (!grew) return previous;
  return [...merged.values()].sort((a, b) => a.id - b.id);
}

/**
 * A run's durable, append-only timeline (FRONT-1500) — the data behind the
 * chat's live "work log" list, distinct from `phase` (`research_runs.phase`,
 * overwritten in place — only ever shows the CURRENT stage). Polls only
 * while `live` (same convention as {@link useResearchCitations}) and reads
 * the query cache itself (`client.getQueryData`, available on the v5
 * `queryFn` context) to accumulate incrementally rather than re-fetching the
 * whole log on every poll — see {@link fetchResearchTimelinePage}.
 */
export function useResearchTimeline(
  runId: number | undefined,
  options: { live?: boolean } = {},
) {
  const queryKey = runId ? researchKeys.timeline(runId) : researchKeys.all;
  return useQuery({
    queryKey,
    queryFn: ({ client }) => {
      const previous =
        client.getQueryData<ResearchTimelineEvent[]>(queryKey) ?? [];
      return fetchResearchTimelinePage(runId!, previous);
    },
    enabled: !!runId,
    refetchInterval: options.live ? 3_000 : false,
  });
}
